/**
 * Device Fingerprinting Service
 * Uses FingerprintJS to generate unique device identifiers
 * for anti-abuse and security purposes
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs'

class FingerprintService {
  private fpPromise: Promise<any> | null = null

  /**
   * Initialize the FingerprintJS agent
   */
  private async getAgent() {
    if (!this.fpPromise) {
      this.fpPromise = FingerprintJS.load()
    }
    return this.fpPromise
  }

  /**
   * Generate a unique device fingerprint
   * @returns {Promise<string>} The device fingerprint hash
   */
  async generateFingerprint(): Promise<string> {
    try {
      const fp = await this.getAgent()
      const result = await fp.get()
      
      // The visitorId is a unique identifier for this device/browser
      console.log('🔐 Device fingerprint generated:', result.visitorId)
      
      return result.visitorId
    } catch (error) {
      console.error('❌ Failed to generate fingerprint:', error)
      // Return a fallback identifier (less secure but ensures app works)
      return this.generateFallbackFingerprint()
    }
  }

  /**
   * Get detailed fingerprint components (for debugging/logging)
   * @returns {Promise<object>} Detailed fingerprint data
   */
  async getFingerprintDetails(): Promise<any> {
    try {
      const fp = await this.getAgent()
      const result = await fp.get()
      
      return {
        visitorId: result.visitorId,
        confidence: result.confidence,
        components: result.components,
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      console.error('❌ Failed to get fingerprint details:', error)
      return null
    }
  }

  /**
   * Fallback fingerprint generation using basic browser info
   * Used when FingerprintJS fails
   */
  private generateFallbackFingerprint(): string {
    const nav = window.navigator
    const screen = window.screen
    
    const data = [
      nav.userAgent,
      nav.language,
      screen.colorDepth,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage
    ].join('|')
    
    // Simple hash function
    let hash = 0
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    
    const fallbackId = `fallback_${Math.abs(hash).toString(36)}`
    console.warn('⚠️ Using fallback fingerprint:', fallbackId)
    
    return fallbackId
  }

  /**
   * Store fingerprint in localStorage for quick access
   */
  async storeFingerprint(): Promise<string> {
    const fingerprint = await this.generateFingerprint()
    localStorage.setItem('device_fingerprint', fingerprint)
    localStorage.setItem('fingerprint_timestamp', new Date().toISOString())
    return fingerprint
  }

  /**
   * Get stored fingerprint or generate new one
   */
  async getOrCreateFingerprint(): Promise<string> {
    const stored = localStorage.getItem('device_fingerprint')
    const timestamp = localStorage.getItem('fingerprint_timestamp')
    
    // Regenerate if older than 30 days
    if (stored && timestamp) {
      const age = Date.now() - new Date(timestamp).getTime()
      const thirtyDays = 30 * 24 * 60 * 60 * 1000
      
      if (age < thirtyDays) {
        console.log('🔐 Using cached fingerprint')
        return stored
      }
    }
    
    console.log('🔐 Generating fresh fingerprint')
    return this.storeFingerprint()
  }
}

// Export singleton instance
export const fingerprintService = new FingerprintService()
